// src/_components/cta-banner.tsx
"use client";
import { useForm, FormProvider } from "react-hook-form";
import Button from "@/components/buttons";
import cn from "@/utils/cn";
import { CTABackground } from "@/assets/svg-icons/svg-icons";
import SignupInputField from "./SignupInputField";
import React, { useState, useEffect } from "react";
import TextComponent from "./TextComponent";
import StandaloneAuditForm from "./StandaloneAuditForm";
import { auditFormSchema, AuditFormData } from "@/utils/schemas/formSchemas";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormSubmission } from "@/app/hooks/useFormSubmission";
import { MdOutlineChevronLeft } from "react-icons/md";
import { useZStore } from "@/utils/z-store"; // Import useZStore
import { trackGAEvent } from "@/utils/ga";

type CTABannerProps = {
  variant?: "full" | "form-only";
  className?: string;
} & React.HTMLAttributes<HTMLDivElement>;

export default function CTABanner({
  variant = "full",
  className,
  ...props
}: CTABannerProps) {
  const { isLoading, submissionStatus, submitForm, setSubmissionStatus } =
    useFormSubmission();
  const [showApiKey, setShowApiKey] = useState(false);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;

    if (submissionStatus.type === "success" && submissionStatus.message) {
      timeoutId = setTimeout(() => {
        setSubmissionStatus({ type: null, message: null });
      }, 10000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [submissionStatus]);

  const methods = useForm<AuditFormData>({
    resolver: zodResolver(auditFormSchema),
    mode: "onChange",
  });

  const { handleSubmit, trigger, watch } = methods;

  const emailValue = watch("email");

  const handleContinue = async () => {
    const isEmailValid = await trigger("email");
    if (isEmailValid) {
      methods.setValue("apiKey", "");
      methods.clearErrors("apiKey"); // Clear any previous API key errors

      setSubmissionStatus({ type: null, message: null });
      setShowApiKey(true);
    }
  };

  const handleGoBack = () => {
    setSubmissionStatus({ type: null, message: null }); // Clear any messages
    setShowApiKey(false);
    methods.setValue("apiKey", ""); // Clear API key value
    methods.clearErrors("apiKey"); // Clear API key errors
  };

  const { actions } = useZStore(); // Destructure actions from useZStore

  const onSubmit = async (data: AuditFormData) => {
    const { success, trackerId } = await submitForm(
      // Destructure trackerId
      data,
      "EAE_FORM",
      "Thank you! We will process your request and send you the report shortly.",
    );
    if (success) {
      // Track successful audit report submission
      trackGAEvent("free_audit_submit", {
        email: data.email,
      });

      methods.reset();
      setShowApiKey(false);
      if (trackerId) {
        actions.updateProcessingStatus({
          status: "processing",
          progress: 7,
          trackerId: trackerId,
        });
      }
    }
  };

  const formContent = (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="relative">
          {/* {submissionStatus?.message && (
            <TextComponent
              type="caption"
              className={cn(
                "absolute lg:-top-6 -top-4 left-5",
                submissionStatus.type === "success"
                  ? "text-[#00E096]"
                  : "text-[#FA5A7D]",
              )}
            >
              {submissionStatus.message}
            </TextComponent>
          )} */}

          <div className={cn("flex items-center justify-center space-x-4")}>
            {!showApiKey ? (
              <>
                <SignupInputField
                  name="email"
                  type="email"
                  placeholder="Email Address"
                  required
                  className={cn(
                    "w-full",
                    "border-[#EDF2F680] bg-[#5B89FE] text-[#FFFFFF] placeholder:text-white",
                  )}
                  containerClassName="w-full md:w-[347px]"
                  // Pass success or apiError from submissionStatus
                  successMessage={
                    submissionStatus.type === "success"
                      ? submissionStatus.message
                      : null
                  }
                  apiError={
                    submissionStatus.type === "error"
                      ? submissionStatus.message
                      : null
                  }
                />

                <Button
                  type="button"
                  variant="primary"
                  onClick={handleContinue}
                  disabled={isLoading || !emailValue} // Use watched emailValue
                  className={cn(
                    "h-[30px] text-nowrap xl:h-[40px]",
                    "bg-white text-kd-lte-blue cursor-pointer",
                  )}
                >
                  {variant === "full" ? "Continue" : "Claim a Free Audit"}
                </Button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleGoBack}
                  aria-label="Go back to email input"
                  tabIndex={!showApiKey ? -1 : 0}
                >
                  <MdOutlineChevronLeft className="size-5 text-white xl:size-6" />
                </button>
                <SignupInputField
                  name="apiKey"
                  type="text"
                  placeholder="Enter your Mailchimp API key"
                  required
                  className={cn(
                    "w-full",
                    "border-[#EDF2F680] bg-[#5B89FE] text-[#FFFFFF] placeholder:text-white",
                  )}
                  containerClassName="w-full md:w-[347px]"
                  // Pass success or apiError from submissionStatus
                  successMessage={
                    submissionStatus.type === "success"
                      ? submissionStatus.message
                      : null
                  }
                  apiError={
                    submissionStatus.type === "error"
                      ? submissionStatus.message
                      : null
                  }
                  tabIndex={!showApiKey ? -1 : 0}
                />

                <Button
                  type="submit"
                  variant="primary"
                  disabled={isLoading}
                  className={cn(
                    "h-[30px] text-nowrap xl:h-[40px]",
                    "bg-white capitalize text-kd-lte-blue",
                  )}
                >
                  {isLoading
                    ? "Sending..."
                    : variant === "full"
                      ? "Send Report"
                      : "Generate Report"}
                </Button>
              </>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );

  if (variant === "form-only") {
    return (
      <div className={cn("", className)} {...props}>
        <div className="my-3 xl:my-5">
          {showApiKey && (
            <TextComponent type="h3" className="z-10 text-center text-white">
              Leverage your existing data and add a{" "}
              <span className="font-medium text-white">Mailchimp API Key</span>{" "}
              to generate a report.
            </TextComponent>
          )}
          {!showApiKey && (
            <TextComponent
              type="h3"
              className="z-10 text-center font-medium text-white"
            >
              Connect your account and get a scored audit of deliverability, engagement and automations, with prioritized fixes.
            </TextComponent>
          )}
        </div>

        <StandaloneAuditForm
          showApiKey={showApiKey}
          setShowApiKey={setShowApiKey}
          className="w-[300px] xl:w-[576px]"
        />
        {!showApiKey && (
          <p className="mt-3 text-center text-sm text-white/80">
            2,400+ audits run · No credit card · Free
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "bg-kd-lte-blue bg-gradient-to-b md:py-[45px] px-8 md:px-[30.5px] xl:px-[96px] space-y-10 md:space-y-0 flex md:flex-row flex-col md:justify-between md:items-center py-8 xl:pt-[68px] xl:pb-[56px] w-full rounded-[15px] relative overflow-hidden",
        className,
      )}
      id="audit-report-form"
      {...props}
    >
      <CTABackground className="absolute -top-0 left-0 z-0 h-full" />
      <>
        {showApiKey && (
          <TextComponent
            type="body"
            className="z-10 text-center text-[#D1D1D6] md:w-[33%] xl:ml-20"
          >
            Leverage your existing data and add a{" "}
            <span className="font-medium text-white">Mailchimp API Key</span> to
            generate a report.
          </TextComponent>
        )}
        {!showApiKey && (
          <p className="z-10 w-[90%] text-[27px] font-semibold leading-9 text-kd-white md:w-3/4 xl:text-[38px] xl:leading-[54px]">
            Get a Free Audit
          </p>
        )}
      </>
      {/* <div className="z-10">{formContent}</div> */}
      <StandaloneAuditForm
        showApiKey={showApiKey}
        setShowApiKey={setShowApiKey}
        className=""
      />
    </div>
  );
}
